import type { ReactElement } from "react";
import { CreditCard, Wallet } from "lucide-react";

/**
 * OperateurLogo — pastille de marque pour les moyens de paiement réels.
 *
 * Les opérateurs disposant d'un vrai logo (Wave, Orange Money, MTN MoMo,
 * Moov Money — logos repris d'EDACI, optimisés en WebP dans
 * /brand/operators/) sont rendus via une image sur fond blanc arrondi. Les
 * autres (Djamo, carte bancaire) gardent une initiale / icône sur fond plein
 * de la couleur de marque. Indexé sur la vraie union `Methode` du backend.
 */

export type Operateur =
  | "WAVE"
  | "ORANGE_MONEY"
  | "MTN_MOMO"
  | "MOOV_MONEY"
  | "DJAMO"
  | "CARTE_BANCAIRE";

interface OperateurMeta {
  /** Libellé court affiché à côté de la pastille. */
  name: string;
  /** Sous-titre marketing. */
  description: string;
  /** Couleur de marque (fond de la pastille initiale). */
  color: string;
  /** Initiale rendue dans la pastille (ignorée si logo ou icon présent). */
  label: string;
  /** Icône à la place de l'initiale (carte / portefeuille). */
  icon?: "card" | "wallet";
  /** URL du vrai logo de marque ; si présent, rendu à la place de l'initiale. */
  logo?: string;
}

export const OPERATEUR_META: Record<Operateur, OperateurMeta> = {
  WAVE: {
    name: "Wave",
    description: "Sans frais, instantané",
    color: "#1DC3F5",
    label: "W",
    logo: "/brand/operators/wave.webp",
  },
  ORANGE_MONEY: {
    name: "Orange Money",
    description: "Validation par code USSD",
    color: "#FF6600",
    label: "OM",
    logo: "/brand/operators/orange-money.webp",
  },
  MTN_MOMO: {
    name: "MTN MoMo",
    description: "Validation par code USSD",
    color: "#FFCC00",
    label: "MTN",
    logo: "/brand/operators/mtn-momo.webp",
  },
  MOOV_MONEY: {
    name: "Moov Money",
    description: "Validation par code USSD",
    color: "#0066B3",
    label: "Moov",
    logo: "/brand/operators/moov-money.webp",
  },
  DJAMO: {
    name: "Djamo",
    description: "Compte et carte Djamo",
    color: "#5B3DF5",
    label: "Dj",
  },
  CARTE_BANCAIRE: {
    name: "Carte bancaire",
    description: "Visa, Mastercard · 3D Secure",
    color: "#1B2A5E",
    label: "CB",
    icon: "card",
  },
};

export function getOperateurMeta(op: Operateur): OperateurMeta {
  return OPERATEUR_META[op];
}

export function OperateurLogo({
  op,
  size = 44,
  className,
}: {
  op: Operateur;
  size?: number;
  className?: string;
}): ReactElement {
  const meta = OPERATEUR_META[op];
  const classes = [
    "rounded-[10px] flex items-center justify-center shrink-0",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  // Vrai logo de marque → image sur fond blanc.
  if (meta.logo) {
    return (
      <div
        className={classes}
        style={{ backgroundColor: "#FFFFFF", width: size, height: size, padding: Math.round(size * 0.12) }}
      >
        <img
          src={meta.logo}
          alt=""
          aria-hidden
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      </div>
    );
  }

  // Sinon (Djamo, carte) → initiale (ou icône) sur fond couleur de marque.
  const textColor = "#FFFFFF";
  return (
    <div
      className={`${classes} font-display font-bold`}
      style={{
        backgroundColor: meta.color,
        color: textColor,
        width: size,
        height: size,
        fontSize: Math.max(11, Math.round(size * 0.32)),
      }}
      aria-hidden
    >
      {meta.icon === "card" ? (
        <CreditCard style={{ width: size * 0.5, height: size * 0.5 }} />
      ) : meta.icon === "wallet" ? (
        <Wallet style={{ width: size * 0.5, height: size * 0.5 }} />
      ) : (
        meta.label
      )}
    </div>
  );
}
